import React from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import customElementBoolAttr from '../../utils/customElementBoolAttr';
import styles from './smart-records-panel.module.css';
import SmartRecordsController from '../../controllers/SmartRecordsController';
import SectionTitle from './SectionTitle';
import { SmartRecord } from './Record';
import terminology from '../../utils/terminology';
import { type SmartRecordCreatableType } from '../../types';
import classNames from 'classnames';
import RefreshButton from './RefreshButton';
// @ts-expect-error see app/assets
import Selectro from 'components/common/Selectro';

const RecordActions: React.FC = ApplicationView(() => {
  const controller = useController(SmartRecordsController);
  return (
    <div className={classNames(styles.recordActions, styles.panelItem, 'mb-5')}>
      <span className={styles.recordAction}>
        <input
          type='checkbox'
          onChange={e => controller.actionToggleSelectAll(e.target.checked)}
          checked={controller.allRecordsSelected}
        />
        Select all
      </span>
      <RefreshButton />
    </div>
  );
});

// Note about selectros in the demo env: they don't appear to do anything (value
// and text don't get passed in from the "select" event). These values are irrelevant
// in the demo env, but it works as expected in the app.
const Fields: React.FC<{ recordType: SmartRecordCreatableType }> =
  ApplicationView(({ recordType }) => {
    const controller = useController(SmartRecordsController);
    const requiredFields =
      controller.userSelectedFieldsForRecordType(recordType);
    const { project, release, feature } =
      controller.fieldsForRecordType(recordType);
    return (
      <div className={styles.fields}>
        {requiredFields.project && (
          <Selectro
            fetchUrl={`/api/v1/products/picker?user_is=contributer&teams=${controller.showTeams(
              recordType
            )}`}
            defaultLabel={`Select ${terminology('product')}`}
            placeholder={`Select ${terminology('product')}`}
            selectedElements={project ? [[project.name, project.id]] : []}
            select={({ value, text }) => {
              controller.actionSetProject(recordType, {
                id: value,
                name: text,
              });
            }}
            multiple={false}
            headersClickable={false}
            classes={styles.selectro}
          />
        )}
        {requiredFields.release && (
          <Selectro
            fetchUrl={`/api/v1/releases/picker?write_access_only=1${
              recordType === 'epics' ? '&for_epic_projects=true' : ''
            }`}
            defaultLabel={`Select ${terminology('release')}`}
            placeholder={`Select ${terminology('release')}`}
            selectedElements={release ? [[release.name, release.id]] : []}
            select={({ value, text }) => {
              controller.actionSetRelease(recordType, {
                id: value,
                name: text,
              });
            }}
            multiple={false}
            headersClickable={false}
            classes={styles.selectro}
          />
        )}
        {requiredFields.feature && (
          <Selectro
            fetchUrl='/api/v1/features/picker'
            defaultLabel={`Select ${terminology('feature')}`}
            placeholder={`Select ${terminology('feature')}`}
            selectedElements={feature ? [[feature.name, feature.id]] : []}
            select={({ value, text }) => {
              controller.actionSetFeature(recordType, {
                id: value,
                name: text,
              });
            }}
            multiple={false}
            headersClickable={false}
            classes={styles.selectro}
          />
        )}
      </div>
    );
  });

const Records: React.FC<{ recordType: SmartRecordCreatableType }> =
  ApplicationView(({ recordType }) => {
    const controller = useController(SmartRecordsController);
    const records = controller.orderRecordsForTypeAsArray(recordType);
    return (
      <div className={styles.records}>
        {records.map(record => {
          return (
            <div key={record.id} className={styles.recordContainer}>
              <input
                type='checkbox'
                checked={controller.recordSelected(record)}
                onChange={e =>
                  controller.actionSetSelected(record.id, e.target.checked)
                }
              />
              <SmartRecord
                record={record}
                onClick={() =>
                  controller.actionSetSelected(
                    record.id,
                    !controller.recordSelected(record)
                  )
                }
              />
            </div>
          );
        })}
      </div>
    );
  });

const RecordTypeSection: React.FC<{
  recordType: SmartRecordCreatableType;
}> = ({ recordType }) => {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <SectionTitle title={recordType} />
        <Fields recordType={recordType} />
      </div>
      <Records recordType={recordType} />
    </div>
  );
};

const RecordCreate: React.FC = () => {
  const controller = useController(SmartRecordsController);
  const {
    availableRecordTypes,
    readyToSubmit,
    recordCount,
    recordSelectedCount,
  } = controller;
  const { submitting } = controller.state;
  return (
    <>
      <RecordActions />
      <div
        className={classNames(styles.selectionCount, styles.panelItem, 'mb-5')}
      >{`${recordSelectedCount} out of ${recordCount} selected`}</div>
      <div
        className={classNames(styles.panelContentList, styles.panelItem)}
        data-testid='smart-records-create'
      >
        {availableRecordTypes.map(recordType => {
          return <RecordTypeSection key={recordType} recordType={recordType} />;
        })}
      </div>
      <div className={classNames(styles.panelFooter, styles.panelItem)}>
        <span className={styles.panelFooterCheckbox}>
          <input
            type='checkbox'
            onChange={e =>
              controller.actionToggleUseConnectorHierarchies(e.target.checked)
            }
            checked={
              controller.useConnectorHierarchies &&
              controller.hasValidConnectorHierarchies()
            }
            disabled={!controller.hasValidConnectorHierarchies()}
          />
          <aha-tooltip placement='top'>
            <div style={{ width: '200px' }}>
              Convert connectors between Aha! shapes into parent-child
              hierarchies
            </div>
          </aha-tooltip>
          Convert connectors to hierarchies
        </span>
        <aha-button
          kind='primary'
          type='submit'
          onClick={async () => await controller.actionCreateRecords()}
          disabled={customElementBoolAttr(!readyToSubmit || submitting)}
        >
          {submitting ? 'Converting...' : 'Convert'}
        </aha-button>
      </div>
    </>
  );
};

export default ApplicationView(RecordCreate);
